import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Stethoscope, Calendar, Video, Users, CheckCircle, Mail, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Expert Support — calm, production-ready early-access page.
 *
 * Replaces the older "Coming Soon!" hero. The page is honest about the
 * timeline ("we're rolling this out gradually") without ever using
 * marketing-style or alarming language. Users can join the early-access
 * list, browse what to expect, and continue using the rest of HealthLoop
 * without friction.
 */
const SPECIALTIES = [
  { label: 'General health', dot: 'bg-emerald-500' },
  { label: 'Nutrition', dot: 'bg-amber-500' },
  { label: 'Mental wellness', dot: 'bg-purple-500' },
  { label: 'Lifestyle coaching', dot: 'bg-sky-500' },
  { label: 'Medication support', dot: 'bg-rose-500' },
  { label: 'Maternal health', dot: 'bg-pink-500' },
];

const FEATURE_CARDS = [
  {
    icon: Video,
    iconClass: 'text-sky-700 bg-sky-100',
    title: 'Virtual consultations',
    body: 'Book video or phone time with verified clinicians and coaches — from home, on your schedule.',
  },
  {
    icon: Users,
    iconClass: 'text-emerald-700 bg-emerald-100',
    title: 'Verified experts',
    body: 'Licensed professionals with credentials we check before they ever appear on the platform.',
  },
  {
    icon: Calendar,
    iconClass: 'text-purple-700 bg-purple-100',
    title: 'Flexible scheduling',
    body: 'Pick times that fit your life. Reschedule without friction. No surprise admin.',
  },
  {
    icon: CheckCircle,
    iconClass: 'text-teal-700 bg-teal-100',
    title: 'Follow-up support',
    body: 'Continuity matters. Follow-ups and personalised plans are part of every conversation.',
  },
];

const ExpertSupport = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  const handleJoin = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/waitlist`,
        { email: value, source: 'expert-support' },
        { withCredentials: true },
      );
      setJoined(true);
      if (res?.data?.already_joined) {
        toast.success("You're already on the list — we'll keep you posted.");
      } else {
        toast.success("You're on the early-access list — we'll be in touch.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not join the list. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-24" data-testid="expert-support-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 text-gray-600 hover:text-gray-900"
          data-testid="expert-support-back"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Hero — calm, honest, no fanfare */}
        <header className="mb-6">
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 shrink-0">
              <Stethoscope className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                Expert Support
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-2 leading-relaxed">
                We're rolling out one-to-one access to verified clinicians, coaches,
                and nutritionists gradually — starting with a small early-access group
                so we get the experience right for everyone.
              </p>
            </div>
          </div>
        </header>

        {/* Early-access form */}
        {!joined ? (
          <Card className="mb-6 border border-emerald-100 bg-emerald-50/40 rounded-2xl">
            <CardContent className="p-5 sm:p-6">
              <p className="text-[11px] font-semibold text-emerald-800 uppercase tracking-widest mb-2">
                Join the early-access list
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                We'll email you the moment Expert Support opens to your region. No
                marketing — only the announcement, and only once.
              </p>
              <form onSubmit={handleJoin} className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Mail className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    disabled={submitting}
                    className="pl-9 h-11 bg-white"
                    data-testid="expert-support-email-input"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="expert-support-submit"
                >
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Notify me
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card
            className="mb-6 border border-emerald-200 bg-emerald-50 rounded-2xl"
            data-testid="expert-support-joined"
          >
            <CardContent className="p-5 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-900">You're on the list.</p>
                <p className="text-xs text-emerald-800 mt-0.5 leading-relaxed">
                  We'll send a single email when Expert Support opens to your region. In the
                  meantime, your daily check-ins keep building your health rhythm.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* What to expect */}
        <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3 px-1">
          What to expect
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {FEATURE_CARDS.map((c) => {
            const Ic = c.icon;
            return (
              <Card
                key={c.title}
                className="border border-gray-100 rounded-2xl shadow-sm"
                data-testid={`expert-support-feature-${c.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2.5">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.iconClass}`}>
                      <Ic className="h-4 w-4" />
                    </span>
                    {c.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-gray-600 leading-relaxed">{c.body}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Specialties */}
        <Card className="mb-6 border border-gray-100 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Specialties at launch</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SPECIALTIES.map((s) => (
                <div
                  key={s.label}
                  className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} aria-hidden="true" />
                  {s.label}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Soft CTA back to the app */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-gray-900 hover:bg-black text-white"
            data-testid="expert-support-dashboard-cta"
          >
            Back to dashboard
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/family-health')}
            data-testid="expert-support-family-cta"
          >
            Explore Family Health
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExpertSupport;
